import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, isEmpty } from 'lodash-es';
import { TcoEntities } from 'client/data/models/cost-to-own';
import { AppraisalEntities } from 'client/data/models/appraisal';
import { formatPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { ShareThisChart } from 'site-modules/shared/components/share-this-chart/share-this-chart';
import { Image } from 'site-modules/shared/components/image/image';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { getDeprecationYears } from 'client/site-modules/shared/utils/appraiser/deprecation-infographic';
import { SOURCES } from 'client/utils/image-helpers';
import { PoweredByEdmunds } from 'site-modules/shared/components/powered-by-edmunds/powered-by-edmunds';
import { VehicleEntities } from 'client/data/models/vehicle-v2';

export function DeprecationInfographic({
  vehicle = null,
  style = null,
  tcoStyle = null,
  isMobile = false,
  isWidget = false,
  className = '',
}) {
  if (isEmpty(vehicle) || isEmpty(tcoStyle)) return null;

  const makeName = get(vehicle, 'makeName', '');
  const makeSlug = get(vehicle, 'makeSlug', '');
  const modelName = get(vehicle, 'modelName', '');
  const modelSlug = get(vehicle, 'modelSlug', '');
  const year = get(vehicle, 'year', '');
  const styleName = get(style, 'styleName');
  const styleId = get(style, 'styleId');
  const totalDepreciation = get(tcoStyle, 'total.depreciation', 0);
  const depreciationYears = getDeprecationYears(tcoStyle, year);

  const shareableWidgetData = {
    widgetName: 'depreciation',
    defaultWidth: 814,
    defaultHeight: 535,
    minWidth: 320,
    minHeight: 370,
    defaultTitle: `${year} ${makeName} ${modelName} Total Depreciation`,
    queryParams: {
      make: makeSlug,
      model: modelSlug,
      year,
      styleId,
    },
  };

  return (
    <div
      className={classnames(
        'deprecation-infographic total-depreciation bg-white rounded px-1 px-md-3 py-1 py-md-1_5',
        className
      )}
      style={isMobile || isWidget ? {} : { borderWidth: '1px', borderStyle: 'solid' }}
    >
      <div className="text-primary-darker size-20 fw-bold d-flex justify-content-between mb-0_25">
        <span>Total Depreciation</span>
        <span>-{formatPriceStringV2(totalDepreciation)}</span>
      </div>
      <div className="text-gray-dark size-12 mb-1">Based on Good Condition for five years</div>
      <div className="d-md-flex justify-content-between align-items-start mb-1_5">
        <div className="mb-0_5 mb-md-0">
          <div className="text-gray-darker size-14 mb-0_25">
            {year} {makeName} {modelName}
          </div>
          <div className="text-gray-dark size-12">{styleName} with no options</div>
        </div>
        <ShareThisChart
          pageTitle={`${year} ${makeName} ${modelName} Total Depreciation`}
          pageUrl={`https://www.edmunds.com/${makeSlug}/${modelSlug}/${year}/appraisal-value/#depreciation`}
          shareText="Share this chart"
          shareableWidgetData={shareableWidgetData}
          trackingId="click_share_widget"
        />
      </div>
      <AspectRatioContainer aspectRatio="16:7">
        <Image
          imageId="/appraisal/deprecation-chart.svg"
          source={SOURCES.STATIC}
          alt="Total Depreciation"
          className="w-100 img-fluid"
          lazy
        />
      </AspectRatioContainer>
      <div className="d-flex justify-content-between mt-1 px-0_5 px-md-1_25 depreciation-list">
        {depreciationYears.map(yearPrice => (
          <div className="text-center" key={`${yearPrice.year}-graphic`}>
            <div
              className={classnames(
                'h6 mb-md-0',
                yearPrice.active ? 'fw-bold text-gray-darker mb-0_25' : 'fw-normal text-gray-dark mb-0_5'
              )}
            >
              {yearPrice.year}
            </div>
            <div className={classnames('text-gray-darker mb-0', yearPrice.active ? 'fw-bold size-lg' : 'fw-normal h5')}>
              {formatPriceStringV2(yearPrice.price)}
            </div>
            <div className={classnames('text-gray-darker h6 mb-0', yearPrice.active ? 'fw-bold' : 'fw-normal')}>
              -{formatPriceStringV2(yearPrice.depreciation)}
            </div>
          </div>
        ))}
      </div>
      {isWidget && <PoweredByEdmunds className="mt-1" />}
    </div>
  );
}

DeprecationInfographic.propTypes = {
  vehicle: VehicleEntities.StyleWithoutSubmodels,
  tcoStyle: TcoEntities.Style,
  style: AppraisalEntities.Style,
  isMobile: PropTypes.bool,
  isWidget: PropTypes.bool,
  className: PropTypes.string,
};
